// Server photo delete tombstone queue (card #4744).
//
// A tasker's photo delete ALWAYS updates the answer and discards the local
// queued copy immediately, online or off (see removePhotoFromQuestion in
// lib/photo_removal.js) -- that half never needs a queue, it's a synchronous
// IndexedDB write. The server-side half -- DELETE /api/v1/field/images,
// removing the Image row + blob for a photo that already landed -- is
// attempted straight away; if that fails (offline, or the request itself
// errors), the url is queued here FIRST so the intent survives a reload, and
// replayed by field_sync_controller alongside syncCards/syncLogs on
// reconnect. Keyed by url (not ++id): a retried delete of the same photo
// never double-queues, it just overwrites the one pending row.
//
// A photo still mid-upload when the delete happens is left to finish
// sending (aborting an in-flight request is out of scope, same call made for
// field_image_queue's background uploads) -- once it lands, the
// field:photo-landed handler checks for a matching tombstone and issues the
// server delete then, exactly as if the delete had happened after the photo
// landed in the first place.

import db from "./field_db"
import { observedFetch, currentLevel } from "./field_network_health"
import { timeoutFor } from "./field_timeout_policy"
import { logEvent, logWarn } from "./field_logger"

const DELETE_URL = "/api/v1/field/images"

let _inFlight = null

// Attempts the server delete immediately; on any failure (network, offline,
// non-2xx other than 404) queues a tombstone instead of raising. Never
// throws -- callers (photo_removal.js) treat this as fire-and-forget.
export async function deleteServerPhoto(url, csrfToken) {
  const result = await _attemptDelete(url, csrfToken)
  if (result === "retry") await queueDeleteTombstone(url)
}

export async function queueDeleteTombstone(url) {
  await db.pending_photo_deletes.put({ url, queuedAt: new Date().toISOString() })
}

// Every photo delete still queued, most recent first -- for display/debugging.
export async function pendingPhotoDeletes() {
  return db.pending_photo_deletes.orderBy("queuedAt").reverse().toArray()
}

// Replays every queued tombstone, oldest first. Safe to call from several
// places at once -- concurrent callers share the one in-flight pass rather
// than posting the same delete twice.
export function syncPhotoDeletes(csrfToken) {
  if (_inFlight) return _inFlight
  _inFlight = _flushPending(csrfToken).finally(() => { _inFlight = null })
  return _inFlight
}

// Called by the field:photo-landed handler (result_editor_controller) when a
// background-uploaded photo gets its server URL AFTER the tasker already
// deleted it locally while it was still sending. If a tombstone is queued
// for the LOCAL url (the only url that existed before landing), re-key it to
// the server url the delete endpoint actually understands, then try to
// flush straight away.
export async function retargetTombstone(localUrl, serverUrl, csrfToken) {
  const row = await db.pending_photo_deletes.get(localUrl)
  if (!row) return
  await db.pending_photo_deletes.delete(localUrl)
  await db.pending_photo_deletes.put({ url: serverUrl, queuedAt: row.queuedAt })
  await syncPhotoDeletes(csrfToken)
}

// -- private --

async function _flushPending(csrfToken) {
  const rows = await db.pending_photo_deletes.orderBy("queuedAt").toArray()
  let sent = 0
  for (const row of rows) {
    const result = await _attemptDelete(row.url, csrfToken)
    if (result === "sent") {
      await db.pending_photo_deletes.delete(row.url)
      sent++
    } else if (result === "abandon") {
      // The response ended before this delete could land (403) -- retrying
      // will never succeed, so stop asking the server and drop the
      // tombstone. The photo already came off the answer locally; this just
      // means the server copy outlives it, same as a response submitted
      // with the photo still attached.
      await db.pending_photo_deletes.delete(row.url)
    }
  }
  return { sent, remaining: rows.length - sent }
}

// "sent" (confirmed deleted, or already gone -- 404 means an earlier attempt
// landed), "abandon" (403 -- the response ended, never going to succeed), or
// "retry" (offline, network error, or an unexpected server response).
async function _attemptDelete(url, csrfToken) {
  let response
  try {
    response = await observedFetch(`${DELETE_URL}?url=${encodeURIComponent(url)}`, {
      method: "DELETE",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken || ""
      },
      credentials: "same-origin",
      signal: AbortSignal.timeout(timeoutFor("json_write_light", currentLevel()))
    }, { kind: "photo_delete", sizeClass: "small" })
  } catch (error) {
    logWarn("photos", "Photo delete deferred -- network", { url, error: error?.name || String(error) }).catch(() => {})
    return "retry"
  }

  if (response.status === 404 || response.ok) {
    logEvent("photos", "Server photo deleted", { url, alreadyGone: response.status === 404 }).catch(() => {})
    return "sent"
  }

  if (response.status === 403) {
    logWarn("photos", "Photo delete abandoned -- response ended", { url }).catch(() => {})
    return "abandon"
  }

  logWarn("photos", "Photo delete deferred -- server rejected", { url, status: response.status }).catch(() => {})
  return "retry"
}
